import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import SEO from "../components/SEO";
import { CALENDLY_URL } from "../lib/site";

/* -------------------------------------------------------------------------
 *  About — the belief page. Not a company bio.
 *  Answers a single question: Why does AlignED exist?
 *  Governed by the Quiet Confidence Principle™ and the Institution Test™.
 * ------------------------------------------------------------------------- */

const WHAT_WE_SAW = [
  "Execution varies.",
  "Follow-through becomes inconsistent.",
  "Successful practices remain isolated.",
  "Knowledge disappears when people change roles.",
  "Leaders spend too much time reacting instead of strengthening systems.",
];

const BELIEFS = [
  {
    title: "Better Leadership Builds Better Systems\u2122",
    body: "Great leadership does more than manage activity. It creates the conditions for consistent execution.",
  },
  {
    title: "Consistent Execution Creates Equity\u2122",
    body: "Students deserve dependable experiences regardless of school, team, adult, or circumstance.",
  },
  {
    title: "Technology Should Strengthen Leadership\u2122",
    body: "AI and software should support human judgment, not replace it.",
  },
  {
    title: "Organizations Should Learn Continuously\u2122",
    body: "Progress should not disappear when people change roles. Learning should compound.",
  },
  {
    title: "Great Ideas Must Become Everyday Practice\u2122",
    body: "An idea only creates impact when it becomes consistent behavior.",
  },
];

const COMMITMENTS = [
  "We will teach before we sell.",
  "We will listen before we recommend.",
  "We will build thoughtfully.",
  "We will improve continuously.",
  "We will respect educators\u2019 time.",
  "We will protect the trust placed in us.",
  "We will never stop learning.",
];

const FUTURE = [
  "Leadership teams see reality more clearly.",
  "Schools execute more consistently.",
  "Knowledge compounds over time.",
  "Successful practices spread naturally.",
  "Organizations learn continuously.",
  "Students experience stronger systems.",
];

export default function About() {
  useEffect(() => {
    // Support hash-anchor navigation (e.g. /about#what-we-believe)
    const { hash } = window.location;
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        // Delay so the section has mounted and the fixed header is measured
        window.setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
    }
  }, []);

  return (
    <>
      <SEO
        title="Why AlignED Exists | AlignED"
        description="AlignED exists to help educational leaders create Consistent Execution™, stronger organizations, and better student experiences — through the AlignED Method™."
      />

      {/* SECTION 1 — HERO */}
      <section className="bg-[#FDFBF7] border-b border-[#0A192F]/10" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-24">
          <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">About AlignED</div>
          <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.05] text-[#0A192F] max-w-4xl">
            Why AlignED Exists
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            We believe educational leaders deserve systems that help them create more consistent execution, stronger organizations, and better student experiences.
          </p>
          <div className="mt-10">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
              data-testid="about-hero-walkthrough-btn"
            >
              <Calendar className="w-4 h-4" strokeWidth={1.5} /> Schedule a Walkthrough
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 2 — WHAT WE SAW */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="about-what-we-saw">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">What We Saw</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight max-w-3xl">
            The challenge is not commitment. The challenge is consistency.
          </h2>
          <p className="mt-8 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            Every school begins the year with talented educators, dedicated leaders, and meaningful goals. But over time, a familiar pattern surfaces:
          </p>
          <ul className="mt-10 divide-y divide-[#0A192F]/10 border-t border-b border-[#0A192F]/10">
            {WHAT_WE_SAW.map((line, i) => (
              <li
                key={line}
                className="py-5 flex items-start gap-5"
                data-testid={`about-saw-${i}`}
              >
                <span className="font-serif-display text-base text-[#38BDF8] mt-0.5 w-8 flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-lg text-[#1A1D20] leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>
          <p className="mt-10 font-serif-display text-2xl sm:text-3xl text-[#52796F] leading-snug max-w-3xl">
            The pattern is not about effort. It is about the difficulty of sustaining consistent execution over time.
          </p>
        </div>
      </section>

      {/* SECTION 2b — LEADERSHIP OBSERVATION (Evidence Principle™) */}
      <section className="py-20 bg-[#FDFBF7] border-y border-[#0A192F]/10" data-testid="about-leadership-observation">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="border-l-2 border-[#D4AF37] pl-6 sm:pl-8">
            <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">Leadership Observation</div>
            <p className="mt-5 text-lg text-[#1A1D20] leading-relaxed">
              Across the schools and districts we have studied and served, one pattern has appeared again and again: dedicated people often know what should happen, but the system does not always make consistent follow-through easy to see, support, or sustain.
            </p>
            <p className="mt-4 text-lg text-[#1A1D20] leading-relaxed">
              AlignED was built around that observation.
            </p>
            <p className="mt-4 font-serif-display text-xl text-[#52796F] leading-snug">
              The challenge was not commitment. The challenge was consistency.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — WHAT WE BELIEVE  (also serves as Manifesto anchor from Home) */}
      <section id="what-we-believe" className="py-24 bg-[#FDFBF7]" data-testid="about-beliefs">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">What We Believe</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Five beliefs guide everything we build.
            </h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {BELIEFS.map((b, i) => (
              <article
                key={b.title}
                className="bg-[#F4F1EB]/60 border border-[#0A192F]/10 p-8"
                data-testid={`about-belief-${i}`}
              >
                <div className="text-xs uppercase tracking-widest text-[#38BDF8]">
                  Belief 0{i + 1}
                </div>
                <h3 className="mt-4 font-serif-display text-2xl text-[#0A192F] leading-snug">
                  {b.title}
                </h3>
                <p className="mt-4 text-[#4A5568] leading-relaxed">{b.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — THE ALIGNED METHOD (concise pointer per New Value Rule™) */}
      <section id="the-method" className="py-24 bg-[#F4F1EB]" data-testid="about-method">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">The AlignED Method&trade;</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
            The framework behind everything we build.
          </h2>
          <p className="mt-8 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            Every idea, system, and leadership conversation at AlignED is guided by the AlignED Method&trade; &mdash; a seven-stage leadership framework designed to help educational organizations create more consistent execution and continuous learning.
          </p>
          <div className="mt-10">
            <Link
              to="/#method-section"
              className="inline-flex items-center gap-2 text-[#0A192F] text-sm tracking-wide border-b border-[#0A192F] pb-1 hover:text-[#52796F] hover:border-[#52796F] transition-colors"
              data-testid="about-method-explore-cta"
            >
              Explore the AlignED Method&trade; <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 5 — OUR COMMITMENT */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="about-commitment">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Our Commitment to Educational Leaders</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight max-w-3xl">
            How we intend to show up.
          </h2>
          <ul className="mt-14 space-y-6 max-w-3xl">
            {COMMITMENTS.map((line, i) => (
              <li
                key={line}
                className="font-serif-display text-xl sm:text-2xl text-[#0A192F] leading-snug border-l-2 border-[#38BDF8] pl-6 py-1"
                data-testid={`about-commitment-${i}`}
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SECTION 6 — THE FUTURE */}
      <section className="py-24 bg-[#0A192F] text-[#FDFBF7]" data-testid="about-future">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">The Future We&apos;re Working Toward</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
              A future where consistency is no longer the exception.
            </h2>
          </div>
          <ul className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
            {FUTURE.map((line, i) => (
              <li
                key={line}
                className="flex items-start gap-4 text-lg text-[#FDFBF7]/90 leading-relaxed"
                data-testid={`about-future-${i}`}
              >
                <span className="w-6 h-px bg-[#D4AF37] mt-3 flex-shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SECTION 7 — FINAL BELIEF */}
      <section className="py-28 bg-[#FDFBF7]" data-testid="about-final">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">Our Belief</div>
          <p className="mt-10 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-[1.15]">
            Better Leadership Builds Better Systems.
          </p>
          <p className="mt-6 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-[1.15]">
            Better Systems Create Better Student Experiences.
          </p>
          <p className="mt-8 font-serif-display text-2xl sm:text-3xl text-[#52796F] leading-[1.2]">
            Every student deserves both.
          </p>

          <div className="mt-14 max-w-2xl mx-auto">
            <p className="text-base text-[#4A5568] leading-relaxed">
              If our beliefs resonate, continue exploring the thinking behind AlignED or start a conversation about what this could look like in your organization.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
                data-testid="about-final-walkthrough-btn"
              >
                <Calendar className="w-4 h-4" strokeWidth={1.5} /> Schedule a Walkthrough
              </a>
              <Link
                to="/insights"
                className="inline-flex justify-center items-center gap-2 bg-transparent text-[#0A192F] px-8 py-3.5 text-sm tracking-wide hover:bg-[#F4F1EB] transition-colors border border-[#0A192F]"
                data-testid="about-final-insights-btn"
              >
                Explore Insights <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>

          <p className="mt-16 text-sm italic text-[#52796F] max-w-2xl mx-auto">
            Thoughtfully built. Continuously refined. Always in service of better leadership.
          </p>
        </div>
      </section>
    </>
  );
}
